import { useState } from 'react';

import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Dialog,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Pencil, Sparkles } from 'lucide-react';

import { DialogStickyHeader } from '@modules/_shared/components/DialogStickyHeader';
import { RaForm } from '@modules/_shared/components/form/RaForm';
import { type TPricingFormData } from '@modules/_shared/components/listing-overview-form/listingOverviewFormDefinition';
import { ListingPriceAppraisalPanel } from '@modules/_shared/components/listing-overview-form/ListingPriceAppraisalPanel';
import {
  ListingPricePreview,
  type LivePriceValues,
} from '@modules/_shared/components/listing-overview-form/ListingPricePreview';
import { type FormDefinition } from '@modules/_shared/components/listing-overview-form/models';
import type { PropertyFormContext } from '@modules/_shared/components/property-form/forms-definitions/propertyDetailsFormDefinition';
import { PropertyTypeProvider } from '@modules/_shared/components/property-form/propertyTypeProvider';
import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import { stickyDialogSx } from '@modules/_shared/components/stickyDialogSx';
import type { GetListingDetailsData } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

type ListingPriceEditDialogProps = {
  listing: GetListingDetailsData;
  formDefinition: FormDefinition<TPricingFormData>;
  propertyTypes: PropertyFormContext['propertyTypes'];
  opened: boolean;
  loading: boolean;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
  setIsFormDirty: (dirty: boolean) => void;
  onAppraised?: () => Promise<unknown> | void;
};

export const ListingPriceEditDialog = ({
  listing,
  formDefinition,
  propertyTypes,
  opened,
  loading,
  canEdit,
  onClose,
  onSaved,
  setIsFormDirty,
  onAppraised,
}: ListingPriceEditDialogProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [liveValues, setLiveValues] = useState<LivePriceValues>(
    () => formDefinition.defaultValues as LivePriceValues,
  );
  const [mobileTab, setMobileTab] = useState<'edit' | 'valuation'>('edit');

  const AnyRaForm = RaForm as React.ComponentType<any>;

  const appraisalPanel = (
    <ListingPriceAppraisalPanel
      propertyId={listing.property.id}
      latestAppraisalId={listing.property.latest_appraisal?.id ?? null}
      countryCode={listing.property.country_code}
      canEdit={canEdit}
      onAppraised={onAppraised}
    />
  );

  return (
    <Dialog
      open={opened}
      fullWidth
      maxWidth="lg"
      onClose={onClose}
      sx={[
        stickyDialogSx,
        {
          '& .MuiDialog-paper': {
            display: 'flex',
            flexDirection: 'column',
            height: { xs: '92vh', md: 'auto' },
            maxHeight: { xs: '92vh', md: '90vh' },
          },
        },
      ]}
      slots={{ transition: SlideUpTransition }}
    >
      <DialogStickyHeader
        title={formDefinition.title}
        onClose={onClose}
        loading={loading}
        sx={{ flexShrink: 0 }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            display: !isMobile || mobileTab === 'edit' ? 'block' : 'none',
          }}
        >
          <AnyRaForm
            formDefinition={formDefinition.formDefinition}
            readOnly={!canEdit}
            defaultValues={formDefinition.defaultValues}
            validate={formDefinition.validate}
            onChange={setLiveValues}
            onSubmit={async (_: unknown, data: TPricingFormData) => {
              const result = await formDefinition.onSubmit(data);
              onSaved();
              return result;
            }}
            onCancel={onClose}
            onFormDirtyChange={setIsFormDirty}
            actionButtonsSlotProps={{
              container: {
                elevation: 0,
                sx: (theme: { palette: { divider: string } }) => ({
                  borderTop: `1px solid ${theme.palette.divider}`,
                }),
              },
            }}
            gridProps={{ sx: { alignItems: 'flex-end' } }}
          >
            <PropertyTypeProvider propertyTypes={propertyTypes} />
          </AnyRaForm>
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: 380 },
            flex: { xs: 1, md: '0 0 380px' },
            minWidth: 0,
            overflowY: 'auto',
            p: 2,
            display: {
              xs: mobileTab === 'valuation' ? 'block' : 'none',
              md: 'block',
            },
            borderLeft: {
              md: theme => '1px solid ' + (theme.vars ?? theme).palette.divider,
            },
          }}
        >
          <Stack spacing={2}>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <ListingPricePreview
                property={listing.property}
                values={liveValues}
              />
            </Box>
            {appraisalPanel}
          </Stack>
        </Box>
      </Box>

      {isMobile && (
        <BottomNavigation
          showLabels
          value={mobileTab}
          onChange={(_, value: 'edit' | 'valuation') => setMobileTab(value)}
          sx={{
            flexShrink: 0,
            borderTop: theme =>
              `1px solid ${(theme.vars ?? theme).palette.divider}`,
          }}
        >
          <BottomNavigationAction
            label={t('Edit')}
            value="edit"
            icon={<Pencil size={20} />}
          />
          <BottomNavigationAction
            label={t('Valuation')}
            value="valuation"
            icon={<Sparkles size={20} />}
          />
        </BottomNavigation>
      )}
    </Dialog>
  );
};
